import { FloatButton, Spin } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Core/Icon';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { useOffice } from '../../hooks/useOffice';

interface OfficeProps {
  type: FileTypeEnum;
  /** Override route param when embedded in the bottom drawer. */
  fileId?: string | number;
  /** Fill parent (drawer body) instead of full viewport under FM header. */
  fillParent?: boolean;
  /** Drawer embed: close sheet on UI_Close / delete instead of navigating. */
  onRequestClose?: () => void;
}

export default function Office({ type, fileId, fillParent, onRequestClose }: OfficeProps) {
  const { t } = useTranslation();
  const {
    isLoading,
    isMoveActionLoading,
    accessToken,
    accessTokenExpiry,
    wopiSource,
    parsedFile,
    canShareFile,
    canMoveFile,
    canOpenContainingFolder,
    canDownloadFile,
    hasPublicFileToken,
    hideHostOwnedFloatActions,
    handleShare,
    handleOpenMoveModal,
    handleOpenContainingFolder,
    handleDownload,
    handleDownloadEncryptedFile,
    aiFileActionsItems,
    isRightPanelOpen,
  } = useOffice({ type, fileId, onRequestClose });

  // OfficeGrant beta: File menu owns host actions (share/move/download/…). Hide the whole float
  // group — including Download and AI. AI still lives in useOffice (RightPanel); add a ribbon
  // entry later if needed.
  const showShareFloat = Boolean(parsedFile && canShareFile);
  const showMoveFloat = Boolean(parsedFile && !hasPublicFileToken && canMoveFile);
  const showOpenContainingFolderFloat = Boolean(parsedFile && canOpenContainingFolder);
  const showDownloadFloat = Boolean(canDownloadFile && parsedFile);
  const showAiFloat = Boolean(aiFileActionsItems && aiFileActionsItems.length > 0);
  const showFloatGroup =
    !hideHostOwnedFloatActions &&
    (showShareFloat ||
      showMoveFloat ||
      showOpenContainingFolderFloat ||
      showDownloadFloat ||
      showAiFloat);

  return (
    // Route: under FM header stack. Drawer: fill parent body box.
    <main
      className={clsx(
        'relative w-full overflow-hidden',
        // fillParent: h-full (drawer host is not flex-col — flex-1 alone collapses to 0).
        fillParent ? 'h-full min-h-0' : 'h-[calc(100dvh-var(--app-header-stack-height,3.5rem))]',
      )}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Spin size="large" spinning />
        </div>
      )}

      <div className="relative h-full overflow-hidden p-0">
        <form
          id="office_form"
          name="office_form"
          target="office_frame"
          action={wopiSource}
          method="post"
        >
          <input name="access_token" value={accessToken} type="hidden" />
          <input name="access_token_ttl" value={accessTokenExpiry} type="hidden" />
        </form>
        <span id="frameholder" className="relative block h-full w-full" />
      </div>

      {showFloatGroup && (
        <FloatButton.Group
          trigger="click"
          type="primary"
          shape="square"
          style={{
            // Clear RightPanel max-w-[480px] (+25px float gap; was 375 when panel was 350).
            right: isRightPanelOpen ? '505px' : '25px',
            transition: 'right 0.3s ease-in-out',
          }}
          icon={<Icon name="DownloadIcon" className="h-5 w-5" />}
        >
          {showShareFloat && (
            <FloatButton
              icon={<Icon name="ShareIcon" className="h-5 w-5" />}
              tooltip={{ title: t('common.share'), placement: 'left' }}
              onClick={handleShare}
            />
          )}
          {showMoveFloat && (
            <FloatButton
              icon={<Icon name="FolderInputIcon" className="h-5 w-5" />}
              tooltip={{ title: t('common.move'), placement: 'left' }}
              className={clsx(
                (isLoading || isMoveActionLoading) && 'pointer-events-none opacity-50',
              )}
              onClick={() => {
                if (isLoading || isMoveActionLoading) return;
                void handleOpenMoveModal();
              }}
            />
          )}
          {showOpenContainingFolderFloat && (
            <FloatButton
              icon={<Icon name="FolderSymlinkIcon" className="h-5 w-5" />}
              tooltip={{ title: t('common.openFileLocation'), placement: 'left' }}
              aria-label={t('common.openFileLocation')}
              className={clsx(isLoading && 'pointer-events-none opacity-50')}
              onClick={() => {
                if (isLoading) return;
                void handleOpenContainingFolder();
              }}
            />
          )}
          {showDownloadFloat && parsedFile && (
            <>
              <FloatButton
                icon={<Icon name="FileDownIcon" className="h-5 w-5" />}
                tooltip={{ title: t('common.download'), placement: 'left' }}
                onClick={handleDownload}
              />
              <FloatButton
                icon={<Icon name="ShieldCheckIcon" className="h-5 w-5" />}
                tooltip={{ title: t('common.downloadEncrypted'), placement: 'left' }}
                onClick={() => handleDownloadEncryptedFile(parsedFile)}
              />
            </>
          )}
          {showAiFloat &&
            aiFileActionsItems
              ?.filter((item): item is NonNullable<typeof item> => item != null && 'key' in item)
              .map((item) => (
                <FloatButton
                  key={item.key}
                  icon={
                    'icon' in item ? (
                      item.icon
                    ) : (
                      <Icon name="BrainCircuitIcon" className="h-5 w-5" />
                    )
                  }
                  tooltip={{
                    title: 'label' in item ? item.label : t('common.AIGrant'),
                    placement: 'left',
                  }}
                  onClick={() => {
                    if ('onClick' in item && typeof item.onClick === 'function') {
                      item.onClick({} as any);
                    }
                  }}
                />
              ))}
        </FloatButton.Group>
      )}
    </main>
  );
}
